/**
 * NOMBRE, PARAMETROS Y DESCRIPCION (MAD-NOMBRE-PARAMETROS-DESCRIPCION-T1, maqueta VDDDk8sxb5T1omNLunuNNs, SI de Josse
 * #44226).
 *
 * Cada texto del item en su lugar, con la regla a la vista:
 *   - el NOMBRE (el escrito a mano o el armado) sale en la tarjeta del paso a paso, en Tus items y como titulo de la
 *     partida; nunca lleva precio (lo quita la base, nexo-api);
 *   - los PARAMETROS (caracteristicas) sirven para encontrar y distinguir; no salen solos en el presupuesto;
 *   - la DESCRIPCION PARA EL CLIENTE sale en el presupuesto y el PDF, debajo del nombre. Hoy es el texto del item de la
 *     plantilla que usa la opcion (el de tu presupuesto viejo); cuando la API publique la escrita a mano, gana esa.
 *
 * Antes la partida que agregaba el paso a paso llevaba como TITULO el texto largo del presupuesto viejo y sin
 * descripcion. Sin DOM: lo prueba scripts/check-nombre-descripcion.mjs.
 *
 * @module application/presupuestos/nombreYDescripcion
 */

const texto = (v) => (v === null || v === undefined ? '' : String(v).replace(/\s+/g, ' ').trim());

/** Donde sale cada parte (las lineas 'Sale en' del editor y el cuadro de la regla). */
export const DONDE_SALE = {
  nombre: 'la tarjeta del paso a paso, Tus ítems y como título de la partida',
  parametros: 'encontrarlo y distinguirlo en el paso a paso; no salen solos en el presupuesto',
  descripcion: 'el presupuesto y el PDF, debajo del nombre'
};

/** Los textos distintos de los items de la plantilla de un estandar (los de tus presupuestos viejos), en su orden. */
export function descripcionesViejas(estandar) {
  const out = [];
  for (const i of Array.isArray(estandar?.items) ? estandar.items : []) {
    const t = texto(i?.descripcion);
    if (t && !out.some((x) => x.toLowerCase() === t.toLowerCase())) out.push(t);
  }
  return out;
}

/**
 * La descripcion para el cliente de una partida que sale de un estandar: la escrita a mano (cuando la API la publique),
 * si no la del item de la plantilla que usa la opcion, si no la primera vieja, si no el texto generado.
 */
export function descripcionParaElCliente(estandar, itemDeLaOpcion = null) {
  return texto(estandar?.descripcionManual) || texto(itemDeLaOpcion?.descripcion)
    || descripcionesViejas(estandar)[0] || texto(estandar?.textoCliente);
}

/** De donde sale la descripcion que se ve (para decirlo en el editor). */
export function origenDeLaDescripcion(estandar) {
  if (texto(estandar?.descripcionManual)) return 'escrita';
  if (descripcionesViejas(estandar).length) return 'vieja';
  return texto(estandar?.textoCliente) ? 'generada' : 'ninguna';
}

/**
 * MAD-EDICION-COHERENTE-T1 (maqueta 9xVkFa2wbUhPrH7NosTaHC, GO #44415): el otro item de la misma linea que ya tiene ese
 * nombre (sin mirar mayusculas ni espacios), o null. id = el item que se edita (no se compara consigo mismo).
 */
export function nombreRepetido(estandaresDeLaLinea, id, nombre) {
  const n = texto(nombre).toLowerCase();
  if (!n) return null;
  return (Array.isArray(estandaresDeLaLinea) ? estandaresDeLaLinea : [])
    .find((e) => e && (!id || e.id !== id) && texto(e.nombre).toLowerCase() === n) || null;
}

/**
 * Las filas de 'Datos para ordenar y buscar': el tipo (si la linea tiene tipos) y cada caracteristica de la plantilla con
 * lo que tiene el item ('—' si nada). En el orden del editor: primero las que distinguen en la linea, despues las demas.
 */
export function filasDeDatos(tipos, caracteristicasEnOrden, borrador) {
  const filas = [];
  if (Array.isArray(tipos) && tipos.length) filas.push({ clave: 'Tipo', valor: texto(borrador?.tipo) || '—', lleno: !!texto(borrador?.tipo), esTipo: true });
  for (const c of Array.isArray(caracteristicasEnOrden) ? caracteristicasEnOrden : []) {
    const v = (borrador?.caracteristicas?.[c.nombre] || []).map(texto).filter(Boolean);
    filas.push({ clave: c.nombre, valor: v.length ? v.join(', ') : '—', lleno: v.length > 0, esTipo: false });
  }
  return filas;
}

/**
 * La descripcion escrita en el editor (nexo-api 802): lo escrito queda como descripcionManual; vacio, o igual a la que
 * ya sale sin escribir (base), vuelve a null (la base usa la generada y la partida la de su item).
 */
export function conDescripcion(borrador, escrito, base = '') {
  const t = texto(escrito);
  return { ...borrador, descripcionManual: t && t !== texto(base) ? t : null };
}

/**
 * Titulo y descripcion de la partida que agrega el paso a paso: titulo = el nombre del estandar (a mano o armado);
 * descripcion = la descripcion para el cliente. Sin estandar, la partida queda como venia (titulo = texto del item).
 */
export function partidaDelEstandar(estandar, itemDeLaOpcion = null) {
  const nombre = texto(estandar?.nombre);
  if (!estandar || !nombre) return { titulo: texto(itemDeLaOpcion?.descripcion), descripcion: '' };
  const descripcion = descripcionParaElCliente(estandar, itemDeLaOpcion);
  return { titulo: nombre, descripcion: descripcion.toLowerCase() === nombre.toLowerCase() ? '' : descripcion };
}
